import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppDispatch, AppThunk } from "@/app/store";
import { fetchCampaignBillingBatches } from "@/api/fetchCampaignBillingBatches/fetchCampaignBillingBatchesApi";
import {
  CampaignBillingBatch,
  FetchCampaignBillingBatchesRequest,
  FetchCampaignBillingBatchesResponse,
} from "@/api/fetchCampaignBillingBatches/types";

interface CampaignBillingBatchesState {
  batches: CampaignBillingBatch[];
  totalCount: number;
  totalActualQuantity: number;
  loading: boolean;
  error: string | null;
}

const initialState: CampaignBillingBatchesState = {
  batches: [],
  totalCount: 0,
  totalActualQuantity: 0,
  loading: false,
  error: null,
};

const campaignBillingBatchesSlice = createSlice({
  name: "campaignBillingBatches",
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setBatchesData: (
      state,
      action: PayloadAction<{
        batches: CampaignBillingBatch[];
        totalCount: number;
        totalActualQuantity: number;
      }>,
    ) => {
      state.batches = action.payload.batches;
      state.totalCount = action.payload.totalCount;
      state.totalActualQuantity = action.payload.totalActualQuantity;
    },
    resetBatches: () => initialState,
  },
});

export const { setLoading, setError, setBatchesData, resetBatches } =
  campaignBillingBatchesSlice.actions;

export const fetchCampaignBillingBatchesAction =
  (requestData: FetchCampaignBillingBatchesRequest): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setLoading(true));
    dispatch(setError(null));
    try {
      const response: FetchCampaignBillingBatchesResponse =
        await fetchCampaignBillingBatches(requestData);
      dispatch(
        setBatchesData({
          batches: response.data.batches,
          totalCount: response.meta?.totalCount || 0,
          totalActualQuantity: response.data.totalActualQuantity,
        }),
      );
    } catch (error) {
      if (error instanceof Error) {
        dispatch(setError(error.message));
      } else {
        dispatch(setError("Failed to fetch campaign billing batches"));
      }
    } finally {
      dispatch(setLoading(false));
    }
  };

export default campaignBillingBatchesSlice.reducer;
